import {
  extendedSinglePropertyLookupTypeGuard,
  LookupRecord,
  multiPropertiesLookupRecordTypeGuard,
  singlePropertyLookupTypeGuard, Variation
} from './ResolutionSchemaDefinition.js';
import { LookupId, PModel, PropertyPath } from '../engine.js';
import { engineUtils } from '../services/EngineUtils.js';
import { Option } from '../option.js';
import { Reference, ResolutionSchema } from './ResolutionSchema.js';
import { VariationOrderGenerator } from './VariationOrderGenerator.js';

type Schema = SchemaItem[];

interface SchemaItem {
  option: PropertyPath;
  properties: PropertyPath[]
}


export class ExplicitResolutionStrategy {
  referenceIdentical: Map<PropertyPath, Option[]>;

  constructor(
    private lookup: LookupRecord,
    private resolutionSchema: ResolutionSchema,
    private reference: Reference
  ) {

  }

  * orderOptionValues(model: PModel, optionsPotentialValues: Map<PropertyPath, Option[]>): Generator<[PropertyPath, Option[]][]> {

    if (!this.lookup.variation.length) {
      // TODO: Handle sorting??
      const entries = Array.from(optionsPotentialValues);
      yield entries;
      return;
    }
    const options: Set<PropertyPath> = this.getVariationOptions();
    const optionCompatibleVariation = this.variantsWithOptions(this.lookup.variation, options);
    const variants = new VariationOrderGenerator(optionCompatibleVariation).getVariation();
    this.buildReferenceIdenticalGroups(options, optionsPotentialValues);
    const allIdentical = this.prepareResult(optionsPotentialValues); // all options with reference values
    yield allIdentical;
    for (let variation of variants) {
      const result = this.prepareResult(optionsPotentialValues);
      const schema: Schema = this.buildOptionPropertiesSchema(variation);
      for (const { option, properties } of schema) {
        const optionValuesCandidates = optionsPotentialValues.get(option);
        const optionReference = this.reference.get(option);
        const optionValues = this.referenceEqualsExceptProperties(optionValuesCandidates, properties, optionReference);
        this.replaceOptionGroup(result, option, optionValues);
      }

      yield result;
    }
  }

  private buildReferenceIdenticalGroups(options: Set<PropertyPath>, optionsPotentialValues: Map<PropertyPath, Option[]>) {
    this.referenceIdentical = new Map<PropertyPath, Option[]>();

    for (const option of options) {
      const reference = this.reference.get(option);
      const keys = Object.keys(reference);
      const optionValues = optionsPotentialValues.get(option) ?? [];
      const referenceIdentical = optionValues.filter(o => keys.every(key =>
          engineUtils.equals(
            engineUtils.getPropertyPathValue(o.model, key),
            reference[key]
          )
        )
      );
      this.referenceIdentical.set(option, referenceIdentical);
    }
  }

  // All variation options contain reference identical groups
  private prepareResult(optionsPotentialValues: Map<PropertyPath, Option[]>): [PropertyPath, Option[]][] {
    const result: [PropertyPath, Option[]][] = []
    for (const [option, values] of optionsPotentialValues) {
      if (this.referenceIdentical.has(option)) {
        result.push([option, this.referenceIdentical.get(option)]);
      } else {
        result.push([option, values]);
      }
    }
    return result;
  }

  private buildOptionPropertiesSchema(variation: LookupId | LookupId[]): Schema {
    const lookupIds = !Array.isArray(variation) ? [variation] : variation;
    const map = new Map<PropertyPath, Set<PropertyPath>>();
    for (const id of lookupIds) {
      const lookup = this.resolutionSchema.safelyGetLookupById(id);
      const option = lookup.option;
      let property;
      if (singlePropertyLookupTypeGuard(lookup)) {
        property = lookup.property;
      } else if (extendedSinglePropertyLookupTypeGuard(lookup)) {
        property = lookup.properties[lookup.key];
      } else {
        throw new Error(`Lookup ${lookup.id} is not supported in variation`)
      }
      let set = map.get(lookup.option);
      if (!set) {
        set = new Set<PropertyPath>();
        map.set(option, set);
      }
      set.add(property);
    }

    const result: Schema = [];
    for (let [option, set] of map) {
      result.push({
        option,
        properties: Array.from(set)
      })
    }
    return result;
  }

  private referenceEqualsExceptProperties(optionValues: Option[], properties: PropertyPath[], optionReference: { [p: string]: any }) {
    const result: Option[] = [];
    for (const optionValue of optionValues) {
      if (this.optionSatisfies(optionValue, optionReference, properties)) {
        result.push(optionValue);
      }
    }
    return result;
  }

  private replaceOptionGroup(result: [PropertyPath, Option[]][], option: string, optionValues: Option[]) {
    for (let entry of result) {
      if (entry[0] === option) {
        entry[1] = optionValues;
        return;
      }
    }
  }

  private optionSatisfies(optionValue: Option, optionReference: Record<string, any>, unEqualProperties: PropertyPath[]) {
    const unEqualSet = new Set<PropertyPath>(unEqualProperties);
    for (const [property, referenceValue] of Object.entries(optionReference)) {
      const optionPropertyValue = engineUtils.getPropertyPathValue(optionValue.model, property);
      const equals = engineUtils.equals(optionPropertyValue, referenceValue);
      if (unEqualSet.has(property)) {
        if (equals) {
          return false;
        }
      } else {
        if (!equals) {
          return false;
        }
      }
    }
    return true;
  }

  private getVariationOptions() {
    return new Set<PropertyPath>(this.reference.keys());
  }

  private variantsWithOptions(variation: Variation, options: Set<PropertyPath>) {
    return this.resolutionSchema.getVariationLookups(variation)
      .filter(lookup => options.has(lookup.option))
      .map(lookup => lookup.id)
    // for (let variant of variation) {
    //   const ids = Array.isArray(variant)
    //   this.resolutionSchema.safelyGetLookupById()
    // }
    // return result;
  }
}
